import Link from "next/link";
import { useState } from "react";
import { jwtDecode } from "jwt-decode";
import ForgotPassword from "./ForgotPassword";
import { pushCleverTapEvent } from "../pushCleverTapEvent";
import authService from "@/lib/authService";

export default function ProfileSidebar({ user, onClose, onLogout }) {
  const [open, setOpen] = useState(true);
  const token = user?.token;
  const credlixTokenId = jwtDecode(token);
  const userAuth = credlixTokenId?.auth || [];
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  if (!open) return null;
  const dashboardUrl = `${process.env.DASHBOARD_URL}${credlixTokenId.jti}`;
  const isFirstTimeVisitor =
    userAuth.length === 1 && userAuth[0] === "VIEW_SITE";

  const handleLogoutClick = async () => {
    try {
      const authToken = token || localStorage.getItem('credlix_auth_token');
      
      if (authToken) {
        await authService.logout(authToken);
      }
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      if (onLogout) onLogout();
      setOpen(false);
      if (onClose) onClose();
    }
  };

  return (
    <div className="fixed top-0 right-0 h-screen overflow-y-auto w-4/5 max-w-sm bg-white shadow-xl border-l border-gray-200 z-[300] animate-slideIn  pb-10">
      {/* Header */}
      <div className="flex items-center justify-between bg-gray-50 p-3 border-b border-[#C4C4C4] bg-[#F1F2F3]">
        <div className="flex items-center gap-2">
          <div className="w-12 h-12 sm:w-10 sm:h-10 bg-[#072F5F] text-white rounded-full flex items-center justify-center font-semibold">
            {user?.email?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="text-gray-500 text-sm">Welcome back!</p>
            <h2 className="font-semibold text-lg sm:text-base text-gray-900">
              {user?.name || user?.email?.split("@")[0]}
            </h2>
          </div>
        </div>
        <button
          onClick={() => {
            setOpen(false);
            onClose();
          }}
          className="text-gray-500 hover:text-gray-700 text-xl ml-1"
        >
          ✕
        </button>
      </div>

      {/* View Dashboard */}
      {isFirstTimeVisitor ? (
        <Link
          href="/export-factoring/buyer-credit-check"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            gtag('event', 'gen_termsheet_click');
            pushCleverTapEvent("gen_termsheet_click",{'PageName': 'Profile_Sidebar'});
          }}          
        >
          <div className="w-full text-left px-3 py-3 flex items-center justify-between gap-3 text-[#288BED] font-medium border-b border-[#C4C4C4] hover:bg-blue-50">
            <div className="flex items-center gap-2">
              <svg
                xmlns="https://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="currentColor"
              >
                <path d="M14 21C13.4477 21 13 20.5523 13 20V12C13 11.4477 13.4477 11 14 11H20C20.5523 11 21 11.4477 21 12V20C21 20.5523 20.5523 21 20 21H14ZM4 13C3.44772 13 3 12.5523 3 12V4C3 3.44772 3.44772 3 4 3H10C10.5523 3 11 3.44772 11 4V12C11 12.5523 10.5523 13 10 13H4ZM9 11V5H5V11H9ZM4 21C3.44772 21 3 20.5523 3 20V16C3 15.4477 3.44772 15 4 15H10C10.5523 15 11 15.4477 11 16V20C11 20.5523 10.5523 21 10 21H4ZM5 19H9V17H5V19ZM15 19H19V13H15V19ZM13 4C13 3.44772 13.4477 3 14 3H20C20.5523 3 21 3.44772 21 4V8C21 8.55228 20.5523 9 20 9H14C13.4477 9 13 8.55228 13 8V4ZM15 5V7H19V5H15Z"></path>
              </svg>
              Generate Termsheet
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z"></path>
            </svg>
          </div>
        </Link>
      ) : (
        <Link href={dashboardUrl} target="_blank" rel="noopener noreferrer"
        onClick={() => {
          gtag('event', 'view_dashboard');
          pushCleverTapEvent("view_dashboard",{'PageName': 'Profile_Sidebar'});
        }}>
          <div className="w-full text-left px-3 py-3 flex items-center justify-between gap-3 text-[#288BED] font-medium border-b border-[#C4C4C4] hover:bg-blue-50">
            <div className="flex items-center gap-2">
              <svg
                xmlns="https://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="currentColor"
              >
                <path d="M14 21C13.4477 21 13 20.5523 13 20V12C13 11.4477 13.4477 11 14 11H20C20.5523 11 21 11.4477 21 12V20C21 20.5523 20.5523 21 20 21H14ZM4 13C3.44772 13 3 12.5523 3 12V4C3 3.44772 3.44772 3 4 3H10C10.5523 3 11 3.44772 11 4V12C11 12.5523 10.5523 13 10 13H4ZM9 11V5H5V11H9ZM4 21C3.44772 21 3 20.5523 3 20V16C3 15.4477 3.44772 15 4 15H10C10.5523 15 11 15.4477 11 16V20C11 20.5523 10.5523 21 10 21H4ZM5 19H9V17H5V19ZM15 19H19V13H15V19ZM13 4C13 3.44772 13.4477 3 14 3H20C20.5523 3 21 3.44772 21 4V8C21 8.55228 20.5523 9 20 9H14C13.4477 9 13 8.55228 13 8V4ZM15 5V7H19V5H15Z"></path>
              </svg>
              View Dashboard
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z"></path>
            </svg>
          </div>
        </Link>
      )}
      {/* Personal Details */}
      <div className="px-3 py-4 border-b border-[#C4C4C4]">
        <h3 className="text-[#6A829F] font-semibold mb-3">Personal Details</h3>
        <div className="space-y-3 text-gray-700 text-sm">
          <div className="flex items-center gap-3">
            <svg
              xmlns="https://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path d="M3 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3ZM20 7.23792L12.0718 14.338L4 7.21594V19H20V7.23792ZM4.51146 5L12.0619 11.662L19.501 5H4.51146Z"></path>
            </svg>
            <div>
              <p className="font-medium text-[#6A829F] text-sm">Email</p>
              <p className="text-baseBlack text-sm break-all">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            {/* Phone Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="#000000"
              className="mt-1"
            >
              <path d="M9.36556 10.6821C10.302 12.3288 11.6712 13.698 13.3179 14.6344L14.2024 13.3961C14.4965 12.9845 15.0516 12.8573 15.4956 13.0998C16.9024 13.8683 18.4571 14.3353 20.0789 14.4637C20.599 14.5049 21 14.9389 21 15.4606V19.9234C21 20.4361 20.6122 20.8657 20.1022 20.9181C19.5723 20.9726 19.0377 21 18.5 21C9.93959 21 3 14.0604 3 5.5C3 4.96227 3.02742 4.42771 3.08189 3.89776C3.1343 3.38775 3.56394 3 4.07665 3H8.53942C9.0611 3 9.49513 3.40104 9.5363 3.92109C9.66467 5.54288 10.1317 7.09764 10.9002 8.50444C11.1427 8.9484 11.0155 9.50354 10.6039 9.79757L9.36556 10.6821ZM6.84425 10.0252L8.7442 8.66809C8.20547 7.50514 7.83628 6.27183 7.64727 5H5.00907C5.00303 5.16632 5 5.333 5 5.5C5 12.9558 11.0442 19 18.5 19C18.667 19 18.8337 18.997 19 18.9909V16.3527C17.7282 16.1637 16.4949 15.7945 15.3319 15.2558L13.9748 17.1558C13.4258 16.9425 12.8956 16.6915 12.3874 16.4061L12.3293 16.373C10.3697 15.2587 8.74134 13.6303 7.627 11.6707L7.59394 11.6126C7.30849 11.1044 7.05754 10.5742 6.84425 10.0252Z"></path>
            </svg>

            <div className="flex-1">
              <div className="flex items-center justify-between w-full">
                <span className="font-medium text-[#6A829F] text-sm">
                  Mobile Number
                </span>
                <span className="text-[#0A7205] font-medium text-sm">
                  Verified
                </span>
              </div>
              <p className="text-baseBlack text-sm">
                {user?.profile?.result?.phoneNumber}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Business Details */}
      {!isFirstTimeVisitor && (
        <div className="px-3 py-4 border-b border-[#C4C4C4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[#6A829F] text-base2 font-semibold">
              Business Details
            </h3>
            {/* <button className="text-[#288BED] text-sm font-medium">
            View Details
          </button> */}
          </div>
          <div className="space-y-3 text-gray-700 text-sm">
            {user?.profile.result.loggedInUserRole.businessName && (
              <div className="flex items-center gap-3">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path d="M21 19H23V21H1V19H3V4C3 3.44772 3.44772 3 4 3H14C14.5523 3 15 3.44772 15 4V19H19V11H17V9H20C20.5523 9 21 9.44772 21 10V19ZM5 5V19H13V5H5ZM7 11H11V13H7V11ZM7 7H11V9H7V7Z"></path>
                </svg>
                <div>
                  <p className="font-medium text-[#6A829F] text-sm">
                    Company Name
                  </p>
                  <p className="text-baseBlack text-sm">{user?.profile.result.loggedInUserRole.businessName}</p>
                </div>
              </div>
            )}
            {user?.profile.result.loggedInUserRole.gstNumber && (
              <div className="flex items-center gap-3">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path d="M21 8V20.9932C21 21.5501 20.5552 22 20.0066 22H3.9934C3.44495 22 3 21.556 3 21.0082V2.9918C3 2.45531 3.4487 2 4.00221 2H14.9968L21 8ZM19 9H14V4H5V20H19V9ZM8 7H11V9H8V7ZM8 11H16V13H8V11ZM8 15H16V17H8V15Z"></path>
                </svg>
                <div>
                  <p className="font-medium text-[#6A829F] text-sm">GSTIN</p>
                  <p className="text-baseBlack text-sm">{user?.profile.result.loggedInUserRole.gstNumber}</p>
                </div>
              </div>
            )}
            {/* <div className="flex items-center gap-3">
          <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM9.71002 19.6674C8.74743 17.6259 8.15732 15.3742 8.02731 13H4.06189C4.458 16.1765 6.71639 18.7747 9.71002 19.6674ZM10.0307 13C10.1811 15.4388 10.8778 17.7297 12 19.752C13.1222 17.7297 13.8189 15.4388 13.9693 13H10.0307ZM19.9381 13H15.9727C15.8427 15.3742 15.2526 17.6259 14.29 19.6674C17.2836 18.7747 19.542 16.1765 19.9381 13ZM4.06189 11H8.02731C8.15732 8.62577 8.74743 6.37407 9.71002 4.33256C6.71639 5.22533 4.458 7.8235 4.06189 11ZM10.0307 11H13.9693C13.8189 8.56122 13.1222 6.27025 12 4.24799C10.8778 6.27025 10.1811 8.56122 10.0307 11ZM14.29 4.33256C15.2526 6.37407 15.8427 8.62577 15.9727 11H19.9381C19.542 7.8235 17.2836 5.22533 14.29 4.33256Z"></path></svg>
            <div>
              <p className="font-medium text-[#6A829F] text-sm">Website</p>
              <p>{user?.website || "www.businessmoglix.com"}</p>
            </div>
          </div> */}
          </div>
        </div>
      )}

      {/* Change Password */}
      <button
           onClick={() => {
            setShowForgotPassword(true);
            gtag('event', 'change_password');
            pushCleverTapEvent("change_password",{'PageName': 'Profile_Sidebar'});
          }}
        className="w-full text-left px-3 py-4 flex items-center gap-3 border-b border-[#C4C4C4] text-[#288BED]"
      >
        <svg
          xmlns="https://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="currentColor"
        >
          <path d="M18 8H20C20.5523 8 21 8.44772 21 9V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V9C3 8.44772 3.44772 8 4 8H6V7C6 3.68629 8.68629 1 12 1C15.3137 1 18 3.68629 18 7V8ZM5 10V20H19V10H5ZM11 14H13V16H11V14ZM7 14H9V16H7V14ZM15 14H17V16H15V14ZM16 8V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V8H16Z"></path>
        </svg>
        <span className="font-medium text-gray-800 text-base">
          Change Password
        </span>
      </button>

      {showForgotPassword && (
        <ForgotPassword
          onClose={() => setShowForgotPassword(false)}
          removeSidebarLogin="removeLoginOption"
        />
      )}

      {/* Logout */}
      <button
        onClick={handleLogoutClick}
        className="w-full text-left px-3 py-4 flex items-center gap-3 hover:bg-gray-50 text-[#072F5F]"
      >
        <svg
          xmlns="https://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="currentColor"
        >
          <path d="M5 22C4.44772 22 4 21.5523 4 21V3C4 2.44772 4.44772 2 5 2H19C19.5523 2 20 2.44772 20 3V6H18V4H6V20H18V18H20V21C20 21.5523 19.5523 22 19 22H5ZM18 16V13H11V11H18V8L23 12L18 16Z"></path>
        </svg>
        <span className="font-medium">Logout</span>
      </button>
    </div>
  );
}
